/**
 * @contract
 *   Назначение: Единый хедер публичного портала OXPA.PRO (эталон oxpa-home.html)
 *   Маршрут:   Главная + все страницы (public) route group
 *   Автор:     AI Agent (Редизайн главной, 2026-08-09)
 *   Дата:      2026-08-09
 *   NOTE:      Нав по решению владельца: Рейтинг, Калькулятор, Вики,
 *              427-ФЗ (/fz-427 — страница готовится), Демо (/login), Войти.
 */
"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";
import { useState } from "react";

const NAV_ITEMS = [
  { href: "/rating", label: "Рейтинг" },
  { href: "/calc", label: "Калькулятор" },
  { href: "/wiki", label: "Вики" },
  { href: "/fz-427", label: "427-ФЗ" },
];

export function PublicHeader() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-[hsl(var(--portal-border))] portal-header-glass">
      <div className="mx-auto flex h-16 w-full max-w-[1120px] items-center justify-between gap-6 px-6">
        {/* Лого — квадратный янтарный маркер, как в эталоне */}
        <Link
          href="/"
          className="flex items-center gap-2.5 font-mono text-lg font-bold tracking-[-0.01em] text-[hsl(var(--portal-text))] hover:no-underline"
          aria-label="OXPA.PRO — на главную"
        >
          <span
            className="h-[13px] w-[13px] flex-none bg-[hsl(var(--portal-accent))] shadow-[0_0_10px_hsl(36_82%_63%/0.5)]"
            aria-hidden="true"
          />
          <span>
            OXPA<span className="font-medium text-[hsl(var(--portal-text-dim))]">.PRO</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Основная навигация">
          {NAV_ITEMS.map((item) => {
            const isActive = pathname.startsWith(item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`font-mono text-[13.5px] transition-colors ${
                  isActive
                    ? "text-[hsl(var(--portal-text))]"
                    : "text-[hsl(var(--portal-text-secondary))] hover:text-[hsl(var(--portal-text))]"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
          <Link
            href="/login"
            className="font-mono text-[13.5px] text-[hsl(var(--portal-text-secondary))] transition-colors hover:text-[hsl(var(--portal-text))]"
          >
            Войти
          </Link>
        </nav>

        {/* CTA + Mobile Toggle */}
        <div className="flex items-center gap-3">
          <Link
            href="/rating"
            className="hidden border border-[hsl(var(--portal-border-hover))] px-5 py-3 font-mono text-[13px] font-semibold text-[hsl(var(--portal-text))] transition-colors hover:border-[hsl(var(--portal-text-dim))] hover:bg-[hsl(var(--portal-surface-2))] sm:block lg:hidden xl:block"
          >
            Открыть рейтинг →
          </Link>
          <button
            className="flex h-[42px] w-[42px] cursor-pointer items-center justify-center border border-[hsl(var(--portal-border-hover))] bg-transparent text-[hsl(var(--portal-text))] lg:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Меню"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <nav
          className="fixed inset-16 z-40 border-t border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg))] px-6 py-8 lg:hidden"
          aria-label="Мобильное меню"
        >
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className="block border-b border-[hsl(var(--portal-border))] py-[18px] font-mono text-lg font-semibold tracking-[0.02em] text-[hsl(var(--portal-text))] transition-colors hover:text-[hsl(var(--portal-accent))]"
            >
              {item.label}
            </Link>
          ))}
          <div className="space-y-4 pt-5">
            <Link href="/login" onClick={() => setMobileOpen(false)}>
              <span className="block bg-[hsl(var(--portal-accent))] py-3 text-center font-mono text-lg font-bold tracking-[0.04em] text-[hsl(var(--portal-accent-on))]">
                Демо
              </span>
            </Link>
            <Link
              href="/login"
              onClick={() => setMobileOpen(false)}
              className="block text-center font-mono text-lg font-semibold text-[hsl(var(--portal-text-secondary))]"
            >
              Войти
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
